import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import rrwebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';
import {
  FaPlay,
  FaPause,
  FaRedo,
  FaLock,
  FaClock,
  FaExclamationTriangle,
  FaBan,
  FaShieldAlt,
  FaKey,
  FaCheckCircle,
  FaFilter
} from 'react-icons/fa';
import './SessionReplayPlayer.css';

const EMPTY_SEGMENTS = [];
const EMPTY_EVENTS = [];

/**
 * Sandboxed Session Replay Playback Player.
 * Strictly adheres to backend replay contracts:
 * Handles states: 'ready', 'held', 'expired', 'incomplete', 'disabled', 'unavailable', 'sampled_out'.
 * Enforces: scriptsExecutable = false, sanitizedReconstruction = true.
 * Fetches and assembles rrweb event stream from segment SAS URLs, then mounts rrweb-player.
 * (API #14)
 */
export default function SessionReplayPlayer({
  session,
  isLoading = false,
  error = null,
  reloadNonce = 0,
  onReload
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [progressPercent, setProgressPercent] = useState(0);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const playTimerRef = useRef(null);

  // rrweb event assembly and player mounting
  const [allEvents, setAllEvents] = useState(EMPTY_EVENTS);
  const [isLoadingSegments, setIsLoadingSegments] = useState(false);
  const [segmentsError, setSegmentsError] = useState(null);
  const rrwebContainerRef = useRef(null);
  const playerRef = useRef(null);

  const segments = useMemo(() => {
    return Array.isArray(session?.segments) ? session.segments : EMPTY_SEGMENTS;
  }, [session?.segments]);

  const status = session?.state || session?.status || 'ready';

  // Dynamic duration calculation from rrweb event stream timestamps
  const durationMs = useMemo(() => {
    if (!Array.isArray(allEvents) || allEvents.length < 2) return 0;
    const firstTs = allEvents[0]?.timestamp || 0;
    const lastTs = allEvents[allEvents.length - 1]?.timestamp || 0;
    return Math.max(0, lastTs - firstTs);
  }, [allEvents]);

  const durationSeconds = useMemo(() => {
    if (durationMs > 0) return Math.max(1, Math.round(durationMs / 1000));
    return session?.durationSeconds || 60;
  }, [durationMs, session?.durationSeconds]);

  // Step 3: Fetch & Assemble Segments
  useEffect(() => {
    if (status !== 'ready' || !segments.length) {
      setAllEvents((prev) => (prev.length === 0 ? prev : EMPTY_EVENTS));
      return;
    }

    let isCancelled = false;
    const fetchSegments = async () => {
      setIsLoadingSegments(true);
      setSegmentsError(null);
      try {
        // 1. Sort segments by sequence
        const sortedSegments = [...segments].sort(
          (a, b) => (a.sequence || 0) - (b.sequence || 0)
        );

        // 2. Fetch all segment event streams in parallel
        const eventChunks = await Promise.all(
          sortedSegments.map(async (seg) => {
            const uri = seg.downloadUri || seg.readSasUrl;
            if (!uri) {
              throw new Error(`Missing download SAS URI for segment ${seg.sequence || 'unknown'}`);
            }
            const res = await fetch(uri);
            if (!res.ok) {
              throw new Error(`Failed to download segment ${seg.sequence || 'unknown'} (HTTP ${res.status})`);
            }
            const data = await res.json();
            if (!Array.isArray(data)) {
              throw new Error(`Segment ${seg.sequence || 'unknown'} did not contain a valid event stream`);
            }
            return data;
          })
        );

        // 3. Flatten into single chronological stream
        if (!isCancelled) {
          const flattened = eventChunks.flat();
          setAllEvents(flattened);
        }
      } catch (err) {
        if (!isCancelled) {
          setSegmentsError(err?.message || 'Failed to download recording event segments');
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingSegments(false);
        }
      }
    };

    fetchSegments();

    return () => {
      isCancelled = true;
    };
  }, [status, segments, reloadNonce]);

  // Step 4: Mount Player (Option B: showController false + playerRef)
  useEffect(() => {
    const container = rrwebContainerRef.current;
    if (status !== 'ready' || !allEvents.length || !container) return;

    container.innerHTML = '';

    try {
      const player = new rrwebPlayer({
        target: container,
        props: {
          events: allEvents,
          autoPlay: false,
          speedOption: [1, 2, 4, 8],
          skipInactive: true,
          mouseTail: true,
          UNSAFE_replayCanvas: false,
          showController: false,
          width: container.clientWidth || 900,
          height: 480
        }
      });
      playerRef.current = player;

      if (player && typeof player.addEventListener === 'function') {
        player.addEventListener('ui-update-current-time', (event) => {
          const timeMs = event?.payload || 0;
          if (durationMs > 0) {
            const targetPercent = Math.min(100, (timeMs / durationMs) * 100);
            setProgressPercent(targetPercent);
          }
        });

        player.addEventListener('finish', () => {
          setIsPlaying(false);
          setProgressPercent(100);
        });
      }
    } catch (err) {
      console.warn('rrwebPlayer initialization error:', err);
    }

    return () => {
      if (playerRef.current?.pause) {
        try { playerRef.current.pause(); } catch (_) {}
      }
      playerRef.current = null;
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [status, allEvents, durationMs]);

  // Playback timer simulation (fallback / synchronization when player has no event emitter)
  useEffect(() => {
    if (isPlaying && status === 'ready') {
      const intervalMs = 100 / playbackSpeed;
      playTimerRef.current = setInterval(() => {
        setProgressPercent((prev) => {
          if (prev >= 100) {
            setIsPlaying(false);
            return 100;
          }
          const next = prev + 0.5;
          if (segments.length > 0) {
            const nextIdx = Math.min(
              segments.length - 1,
              Math.floor((next / 100) * segments.length)
            );
            setCurrentSegmentIndex(nextIdx);
          }
          return next;
        });
      }, intervalMs);
    } else {
      clearInterval(playTimerRef.current);
    }

    return () => clearInterval(playTimerRef.current);
  }, [isPlaying, playbackSpeed, status, segments.length]);

  const handleTogglePlay = () => {
    if (progressPercent >= 100) {
      setProgressPercent(0);
      setCurrentSegmentIndex(0);
      try { playerRef.current?.goto?.(0); } catch (_) {}
    }
    setIsPlaying((prev) => {
      const next = !prev;
      try {
        if (next) {
          playerRef.current?.play?.();
        } else {
          playerRef.current?.pause?.();
        }
      } catch (_) {}
      return next;
    });
  };

  const handleSeek = (e) => {
    const newPercent = Number(e.target.value);
    setProgressPercent(newPercent);
    if (segments.length > 0) {
      const idx = Math.min(
        segments.length - 1,
        Math.floor((newPercent / 100) * segments.length)
      );
      setCurrentSegmentIndex(idx);
    }
    if (durationMs > 0 && playerRef.current?.goto) {
      try {
        playerRef.current.goto((newPercent / 100) * durationMs);
      } catch (_) {}
    }
  };

  const handleRestart = () => {
    setProgressPercent(0);
    setCurrentSegmentIndex(0);
    try {
      playerRef.current?.goto?.(0);
    } catch (_) {}
  };

  const handleSetSpeed = (spd) => {
    setPlaybackSpeed(spd);
    try {
      if (playerRef.current?.setSpeed) {
        playerRef.current.setSpeed(spd);
      } else if (playerRef.current?.$set) {
        playerRef.current.$set({ speed: spd });
      }
    } catch (_) {}
  };

  // Time-anchored keyframe markers
  const keyframeMarkers = useMemo(() => {
    if (allEvents.length > 1 && durationMs > 0) {
      const firstTs = allEvents[0]?.timestamp || 0;
      const fullSnapshots = allEvents.filter((ev) => ev.type === 2);
      if (fullSnapshots.length > 0) {
        return fullSnapshots.map((ev, idx) => {
          const offsetMs = Math.max(0, ev.timestamp - firstTs);
          const posPercent = Math.min(100, Math.max(0, (offsetMs / durationMs) * 100));
          return {
            id: `kf-ev-${idx}`,
            posPercent,
            timeMs: offsetMs,
            label: `Keyframe Snapshot at ${Math.round(offsetMs / 1000)}s`
          };
        });
      }
    }

    return segments
      .map((seg, idx) => {
        if (seg.chunkType !== 'keyframe') return null;
        const posPercent = segments.length > 1 ? (idx / (segments.length - 1)) * 100 : 0;
        return {
          id: seg.segmentId || `kf-seg-${idx}`,
          posPercent,
          timeMs: durationMs > 0 ? (posPercent / 100) * durationMs : 0,
          label: `Keyframe Segment ${seg.sequence || idx + 1}`
        };
      })
      .filter(Boolean);
  }, [allEvents, segments, durationMs]);

  const handleJumpToKeyframe = (marker) => {
    setProgressPercent(marker.posPercent);
    if (playerRef.current?.goto) {
      try { playerRef.current.goto(marker.timeMs); } catch (_) {}
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const currentSeconds = Math.round((progressPercent / 100) * durationSeconds);

  if (isLoading) {
    return (
      <div className="replay-player-card loading-card" data-testid="replay-player-loading">
        <div className="spinner" />
        <p>Loading session replay recording segments & SAS authorization...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="replay-player-card error-card" data-testid="replay-player-error" role="alert">
        <FaExclamationTriangle className="state-icon red" />
        <h4>Failed to Load Replay Session</h4>
        <p>{error}</p>
        {onReload && (
          <button type="button" className="replay-retry-btn" onClick={onReload}>
            <FaRedo /> Retry
          </button>
        )}
      </div>
    );
  }

  if (!session) {
    return null;
  }

  // State 2: Legal Hold (Compliant playback restricted)
  if (status === 'held') {
    return (
      <div className="replay-player-card held-card" data-testid="replay-held-banner" role="alert">
        <div className="state-icon-box amber">
          <FaLock />
        </div>
        <div className="state-content">
          <h4>Compliance Legal Hold Active</h4>
          <p className="state-desc">
            Under legal hold. Playback restricted.
          </p>
          <span className="state-meta">
            This recording is under a legal hold. Playback is restricted. Audit Retention: Data is cryptographically immutabilized and retained for compliance review.
          </span>
        </div>
      </div>
    );
  }

  // State 3: Expired (14-day server-side retention cap)
  if (status === 'expired') {
    return (
      <div className="replay-player-card expired-card" data-testid="replay-expired-banner" role="alert">
        <div className="state-icon-box slate">
          <FaClock />
        </div>
        <div className="state-content">
          <h4>Recording Expired</h4>
          <p className="state-desc">
            Recording expired (14-day retention limit).
          </p>
          <span className="state-meta">
            This recording has expired. Recordings are kept for 14 days. Per DAP multi-tenant blob storage governance, unheld session chunks are automatically purged after 14 UTC days.
          </span>
        </div>
      </div>
    );
  }

  // State 4: Incomplete (Session ended abruptly)
  if (status === 'incomplete') {
    return (
      <div className="replay-player-card incomplete-card" data-testid="replay-incomplete-banner" role="alert">
        <div className="state-icon-box red">
          <FaExclamationTriangle />
        </div>
        <div className="state-content">
          <h4>Incomplete Recording</h4>
          <p className="state-desc">
            Recording was aborted or incomplete.
          </p>
          <span className="state-meta">
            This recording ended before the session completed. Segments may be truncated or missing terminal checkpoints due to unexpected navigation or window closure.
          </span>
        </div>
      </div>
    );
  }

  // State 5: Disabled (Session Replay not entitled or turned off)
  if (status === 'disabled') {
    return (
      <div className="replay-player-card disabled-card" data-testid="replay-disabled-banner" role="alert">
        <div className="state-icon-box slate">
          <FaBan />
        </div>
        <div className="state-content">
          <h4>Session Replay Disabled</h4>
          <p className="state-desc">
            Session replay is disabled for this site.
          </p>
          <span className="state-meta">
            Session replay is not enabled for this site collection. Enable Session Replay under Site Collection Policies. Note that Heatmap, Customer Analytics, and provisioned storage are prerequisites.
          </span>
        </div>
      </div>
    );
  }

  // State 6: Unavailable (No recording found for session)
  if (status === 'unavailable') {
    return (
      <div className="replay-player-card unavailable-card" data-testid="replay-unavailable-banner" role="alert">
        <div className="state-icon-box slate">
          <FaBan />
        </div>
        <div className="state-content">
          <h4>No Recording Found</h4>
          <p className="state-desc">
            No replay recording found for this session.
          </p>
          <span className="state-meta">
            No replay recording found for this session. Segments are unavailable or have not been ingested.
          </span>
        </div>
      </div>
    );
  }

  // State 7: Sampled Out (Session excluded based on sampling quota)
  if (status === 'sampled_out') {
    return (
      <div className="replay-player-card sampled-card" data-testid="replay-sampled-out-banner" role="alert">
        <div className="state-icon-box slate">
          <FaFilter />
        </div>
        <div className="state-content">
          <h4>Session Excluded by Sampling Policy</h4>
          <p className="state-desc">
            This session was excluded from recording based on the sampling quota.
          </p>
          <span className="state-meta">
            Interaction telemetry was logged, but DOM recording was skipped because this session fell outside the active recording sample rate ({session?.samplingRatePercent || 10}%).
          </span>
        </div>
      </div>
    );
  }

  // State 1: Ready (Interactive Playback in Sandboxed Container)
  const currentSegment = segments[currentSegmentIndex] || null;

  return (
    <div className="replay-player-card ready-player" data-testid="session-replay-player">
      {/* Player Header */}
      <div className="player-top-header">
        <div className="player-title-col">
          <div className="session-id-row">
            <span className="player-kicker">Sanitized Sandboxed Player</span>
            <code className="session-id-badge" data-testid="player-session-id">
              {session?.sessionId || 'session-active'}
            </code>
          </div>
          <span className="recording-id-text">
            Recording: {session?.recordingId || 'rec-default'}
          </span>
        </div>

        {/* Security & Sandbox Badges */}
        <div className="sandbox-security-pills">
          <span className="security-pill" data-testid="sandbox-scripts-badge">
            <FaShieldAlt className="pill-icon" /> scriptsExecutable: false
          </span>
          <span className="security-pill green" data-testid="sanitized-reconstruction-badge">
            <FaCheckCircle className="pill-icon" /> Sanitized Reconstruction
          </span>
        </div>
      </div>

      {/* Sandboxed Stage Screen */}
      <div className="player-stage-viewport" data-testid="replay-stage-viewport">
        {/* Masked Placeholders Notice */}
        <div className="masked-notice-bar" data-testid="masked-content-notice">
          <span>Sanitized session playback (PII masked, scripts inert) &bull; <code>[PASSWORD INPUT - MASKED]</code> & <code>[IFRAME - CONTENT INACCESSIBLE]</code></span>
        </div>

        <div className="stage-sandboxed-canvas" data-testid="sandboxed-canvas">
          <div className="sandboxed-playback-viewport">
            {segmentsError ? (
              <div className="replay-no-segments-overlay error-state" data-testid="replay-segments-error">
                <FaExclamationTriangle className="no-segments-icon red" />
                <p>{segmentsError}</p>
                {onReload && (
                  <button type="button" className="replay-retry-btn" onClick={onReload}>
                    <FaRedo /> Retry Download
                  </button>
                )}
              </div>
            ) : allEvents.length > 0 ? (
              <div
                className="rrweb-player-container"
                ref={rrwebContainerRef}
                data-testid="rrweb-player-container"
              />
            ) : (
              <>
                <iframe
                  className="sandboxed-replay-iframe"
                  sandbox="allow-same-origin"
                  title="Sanitized Session Replay Viewport"
                  data-testid="sandboxed-replay-iframe"
                  srcDoc="<!DOCTYPE html><html><head><meta charset='utf-8'><style>body { margin:0; display:flex; align-items:center; justify-content:center; height:100vh; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; color:#64748b; background:#0b1120; }</style></head><body><div id='replay-root'></div></body></html>"
                />
                {isLoadingSegments ? (
                  <div className="replay-no-segments-overlay" data-testid="replay-loading-segments">
                    <div className="spinner" />
                    <p>Downloading and assembling recording stream ({segments.length} segments)...</p>
                  </div>
                ) : segments.length === 0 ? (
                  <div className="replay-no-segments-overlay" data-testid="replay-no-segments">
                    <FaShieldAlt className="no-segments-icon" />
                    <p>No recording segments returned for this session recording.</p>
                  </div>
                ) : (
                  <div className="replay-active-segment-info">
                    <span>
                      Segment {currentSegmentIndex + 1} of {segments.length} ({currentSegment?.chunkType || 'keyframe'})
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Player Timeline & Controls Bar */}
      <div className="player-controls-card">
        {/* Scrubber & Keyframe Markers */}
        <div className="timeline-container">
          <input
            type="range"
            min="0"
            max="100"
            step="0.1"
            value={progressPercent}
            onChange={handleSeek}
            className="timeline-slider"
            aria-label="Replay timeline progress"
            data-testid="replay-timeline-slider"
          />

          {/* Keyframe tick marks */}
          <div className="keyframe-markers-track">
            {keyframeMarkers.map((kf, idx) => (
              <button
                key={kf.id || idx}
                type="button"
                className="keyframe-marker-dot"
                style={{ left: `${kf.posPercent}%` }}
                title={kf.label}
                onClick={() => handleJumpToKeyframe(kf)}
                data-testid={`keyframe-marker-${idx}`}
              >
                <FaKey className="marker-icon" />
              </button>
            ))}
          </div>
        </div>

        {/* Buttons Row */}
        <div className="controls-button-row">
          <div className="left-controls">
            <button
              type="button"
              className="ctrl-btn play-btn"
              onClick={handleTogglePlay}
              data-testid="replay-play-pause-btn"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>

            <button
              type="button"
              className="ctrl-btn"
              onClick={handleRestart}
              title="Restart"
              aria-label="Restart replay"
            >
              <FaRedo />
            </button>

            <span className="playback-timer" data-testid="playback-timer-display">
              {formatTime(currentSeconds)} / {formatTime(durationSeconds)}
            </span>
          </div>

          <div className="right-controls">
            {/* Playback speed selector */}
            <div className="speed-selector-group">
              <span className="speed-label">Speed:</span>
              {[1, 2, 4].map((spd) => (
                <button
                  key={spd}
                  type="button"
                  className={`speed-btn ${playbackSpeed === spd ? 'active' : ''}`}
                  onClick={() => handleSetSpeed(spd)}
                  data-testid={`speed-btn-${spd}x`}
                >
                  {spd}x
                </button>
              ))}
            </div>

            {/* Current Segment Badge */}
            {currentSegment && (
              <span className="segment-active-pill" data-testid="active-segment-badge">
                Chunk: {currentSegment.chunkType || 'keyframe'} (#{currentSegment.sequence || currentSegmentIndex + 1})
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

SessionReplayPlayer.propTypes = {
  session: PropTypes.shape({
    sessionId: PropTypes.string,
    status: PropTypes.oneOf(['ready', 'held', 'expired', 'incomplete', 'disabled', 'unavailable', 'sampled_out']),
    state: PropTypes.string,
    recordingId: PropTypes.string,
    durationSeconds: PropTypes.number,
    samplingRatePercent: PropTypes.number,
    segments: PropTypes.arrayOf(PropTypes.shape({
      segmentId: PropTypes.string,
      sequence: PropTypes.number,
      chunkType: PropTypes.string,
      readSasUrl: PropTypes.string,
      downloadUri: PropTypes.string,
      expiresAtUtc: PropTypes.string
    }))
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  reloadNonce: PropTypes.number,
  onReload: PropTypes.func
};
